import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { Typography, IconButton, TextField, Button } from '@mui/material';
import { X } from 'lucide-react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { toast } from 'react-toastify';
import styles from './GateRequestModal.module.css';

const EMPTY_FORM = {
  requestType: '',
  reason: '',
  remark: '',
  date: '',
  fromDate: '',
  toDate: '',
  numberOfDays: ''
};

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

/** Converts an ISO date string (or already-plain "YYYY-MM-DD") to a value the <input type="date"> can use. */
const toDateInputValue = (value) => {
  if (!value) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const date = new Date(value);
  return isNaN(date.getTime()) ? '' : date.toISOString().split('T')[0];
};

/** Maps a raw request record (from the table) into the modal's form shape. */
const mapRecordToForm = (data) => {
  const requestType = (data.requestType || '').toLowerCase().replace(/\s+/g, '_');
  const reason = data.reason || '';
  const remark = data.remark && data.remark !== 'Not Available (N/A)' ? data.remark : '';

  const fromDate = toDateInputValue(data.fromDate);
  const toDate = toDateInputValue(data.toDate);
  const singleDate = fromDate || toDate;

  return {
    requestType,
    reason,
    remark,
    date: singleDate,
    fromDate,
    toDate,
    numberOfDays: ''
  };
};

/** Validates the form for the given request type, returns { valid, errors }. */
const validate = (form) => {
  const errors = {};

  if (!form.remark || !form.remark.trim()) errors.remark = 'Remark is required';

  if (form.requestType === 'leave') {
    if (!form.fromDate) errors.fromDate = 'Please select from date';
    if (!form.toDate) errors.toDate = 'Please select expected arrival date';
    if (form.fromDate && form.toDate && new Date(form.toDate) < new Date(form.fromDate)) {
      errors.toDate = 'Arrival date cannot be before from date';
    }
  } else if (!form.date) {
    errors.date = 'Please select a date';
  }

  return { valid: Object.keys(errors).length === 0, errors };
};

/** Builds the API payload — the edit-gatepass endpoint only accepts fromDate, toDate and remark. */
const buildEditPayload = (form) => {
  const isLeave = form.requestType === 'leave';
  return {
    fromDate: isLeave ? form.fromDate : form.date,
    toDate: isLeave ? form.toDate : form.date,
    remark: form.remark.trim()
  };
};

/** Locks body/root scroll while the modal is open. */
const useScrollLock = (locked) => {
  useEffect(() => {
    if (!locked) return undefined;

    const scrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
    const rootEl = document.getElementById('root');
    const mainEl = document.querySelector('main');

    if (rootEl) {
      rootEl.style.overflow = 'hidden';
      rootEl.style.height = '100%';
    }
    if (mainEl) {
      mainEl.style.overflow = 'hidden';
    }
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';

    return () => {
      if (rootEl) {
        rootEl.style.overflow = '';
        rootEl.style.height = '';
      }
      if (mainEl) {
        mainEl.style.overflow = '';
      }
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollY);
    };
  }, [locked]);
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
/**
 * Admin-side "Edit Gate Pass Request" modal.
 * Shows the full request read-only, and lets the admin update only the
 * date(s) and remark. Calls PUT /gate_pass/edit-gatepass/{id} with
 * { fromDate, toDate, remark }.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - selectedData: the gate pass record being edited (must include _id)
 *  - fetchRequests: () => void   // called after a successful update to refresh the list
 */
const AdminEditGatePassModal = ({ open, onClose, selectedData, fetchRequests }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useScrollLock(open);

  // Student info comes straight from the record being edited (admin isn't the student).
  const student = selectedData?.student || {};
  const studentName = student.name || '—';
  const studentUid = student.UID || '—';
  const buildingNo = student.building_no || 'Not Available (N/A)';
  const roomNo = student.roomNo || student.roomNumber || 'Not Available (N/A)';
  const category = student.category || 'Not Available (N/A)';

  // Populate the form whenever a new record is opened for editing.
  useEffect(() => {
    if (open && selectedData) {
      setForm(mapRecordToForm(selectedData));
      setFieldErrors({});
    } else if (!open) {
      setForm(EMPTY_FORM);
      setFieldErrors({});
    }
  }, [open, selectedData]);

  // Auto-calculate number of days for leave requests.
  useEffect(() => {
    if (form.fromDate && form.toDate) {
      const from = new Date(form.fromDate);
      const to = new Date(form.toDate);
      const diffDays = to >= from ? Math.floor((to - from) / 86400000) + 1 : 0;
      setForm((prev) => (prev.numberOfDays !== diffDays ? { ...prev, numberOfDays: diffDays } : prev));
    } else {
      setForm((prev) => (prev.numberOfDays ? { ...prev, numberOfDays: '' } : prev));
    }
  }, [form.fromDate, form.toDate]);

  const handleChange = (key, value) => {
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const requestId = selectedData?._id;
    if (!requestId) {
      toast.error('Missing request id — cannot update this request');
      return;
    }

    const { valid, errors } = validate(form);
    setFieldErrors(errors);
    if (!valid) return;

    const payload = buildEditPayload(form);
    const token = Cookies.get('Token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const updateUrl = `${process.env.REACT_APP_BACKEND_URL}/gate_pass/admin/edit-gatepass/${requestId}`;

    try {
      setSubmitting(true);
      await axios.put(updateUrl, payload, { headers });
      toast.success('Gate pass request updated successfully');
      onClose?.();
      fetchRequests?.();
    } catch (error) {
      const message = error?.response?.data?.message || error?.message || 'Failed to update request';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const isLeave = form.requestType === 'leave';

  return ReactDOM.createPortal(
    <div className={styles.backdrop}>
      <div className={styles.modal} role="dialog" aria-modal="true">
        <div className={styles.header}>
          <Typography className={styles.title}>Edit Gate Pass Request</Typography>
          <IconButton onClick={onClose} size="small" aria-label="Close">
            <X size={20} />
          </IconButton>
        </div>

        <div className={styles.content}>
          {/* Student Information */}
          <section className={styles.section}>
            <Typography className={styles.sectionTitle}>Student Information</Typography>
            <div className={styles.infoGrid}>
              <div>
                <p className={styles.label}>Student Name</p>
                <p className={styles.value} style={{ textTransform: 'capitalize' }}>{studentName}</p>
              </div>
              <div>
                <p className={styles.label}>UID</p>
                <p className={styles.value}>{studentUid}</p>
              </div>
              <div>
                <p className={styles.label}>Building No.</p>
                <p className={styles.value}>{buildingNo}</p>
              </div>
              <div>
                <p className={styles.label}>Category</p>
                <p className={styles.value}>{category}</p>
              </div>
            </div>
          </section>

          {/* Request Details (read-only context) */}
          <section className={styles.section}>
            <Typography className={styles.sectionTitle}>Request Details</Typography>
            <div className={styles.infoGrid}>
              <div>
                <p className={styles.label}>Request Type</p>
                <p className={styles.value} style={{ textTransform: 'capitalize' }}>
                  {form.requestType ? form.requestType.replace('_', ' ') : '—'}
                </p>
              </div>
              <div>
                <p className={styles.label}>Reason</p>
                <p className={styles.value}>{form.reason || 'Not Available (N/A)'}</p>
              </div>
            </div>
          </section>

          {/* Editable fields: Date(s) + Remark */}
          <section className={styles.section}>
            <Typography className={styles.sectionTitle}>{isLeave ? 'Leave Details' : 'Date Details'}</Typography>

            <div className={styles.timeGrid}>
              {isLeave ? (
                <>
                  <TextField
                    label="From Date"
                    type="date"
                    value={form.fromDate}
                    onChange={(e) => handleChange('fromDate', e.target.value)}
                    fullWidth
                    size="small"
                    className={styles.roundedInput}
                    error={Boolean(fieldErrors.fromDate)}
                    helperText={fieldErrors.fromDate}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label="Expected Arrival Date"
                    type="date"
                    value={form.toDate}
                    onChange={(e) => handleChange('toDate', e.target.value)}
                    fullWidth
                    size="small"
                    className={styles.roundedInput}
                    error={Boolean(fieldErrors.toDate)}
                    helperText={fieldErrors.toDate}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label="Number of Days"
                    type="number"
                    value={form.numberOfDays}
                    fullWidth
                    size="small"
                    className={styles.roundedInput}
                    inputProps={{ readOnly: true }}
                  />
                </>
              ) : (
                <TextField
                  label="Date"
                  type="date"
                  value={form.date}
                  onChange={(e) => handleChange('date', e.target.value)}
                  fullWidth
                  size="small"
                  className={styles.roundedInput}
                  error={Boolean(fieldErrors.date)}
                  helperText={fieldErrors.date}
                  InputLabelProps={{ shrink: true }}
                />
              )}
            </div>

            <Typography className={styles.sectionReason} sx={{ mt: 2 }}>
              Remark for Edit<span className={styles.required}>*</span>
            </Typography>
            <TextField
              placeholder="Enter remark for this edit.."
              value={form.remark}
              onChange={(e) => handleChange('remark', e.target.value)}
              fullWidth
              size="small"
              multiline
              minRows={4}
              error={Boolean(fieldErrors.remark)}
              helperText={fieldErrors.remark}
              sx={{
                '& .MuiInputBase-input::placeholder': { color: '#9CA3AF', opacity: 1 },
                '& textarea::placeholder': { color: '#9CA3AF', opacity: 1 }
              }}
            />
          </section>
        </div>

        <div className={styles.footer}>
          <Button onClick={onClose} variant="outlined" className={styles.cancelBtn}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting} variant="contained" className={styles.submitBtn}>
            {submitting ? 'Updating...' : 'Update Request'}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AdminEditGatePassModal;